import React, { useContext, useEffect, useState } from "react";
import {
  Activity,
  ArrowRight,
  Clock3,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AuthContext } from "./AuthContext";
import { API_BASE_URL } from "./api";
import { Button, MetricCard, SectionHeader, StatusPill, SurfaceCard } from "./components/designSystem";
import { DualCurrencyValue, formatUsd, useExchangeRate } from "./utils/currencyDisplay";

export default function Analytics({ refresh }) {
  const { token, user } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { usdToNgnRate } = useExchangeRate();

  useEffect(() => {
    let active = true;

    async function fetchAnalytics() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_BASE_URL}/api/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to load analytics.");
        }

        if (active) {
          setAnalytics(data);
        }
      } catch (fetchError) {
        if (active) {
          setError(fetchError.message || "Failed to load analytics.");
          setAnalytics(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    if (token) {
      fetchAnalytics();
    }

    return () => {
      active = false;
    };
  }, [refresh, token]);

  const isFreePlan = !user?.plan || user.plan === "free";
  const hasData = analytics && !analytics.message;
  const equityCurve = hasData && Array.isArray(analytics.equityCurve) ? analytics.equityCurve : [];
  const holdDistribution = hasData && Array.isArray(analytics.holdDistribution) ? analytics.holdDistribution : [];
  const perAsset = hasData && Array.isArray(analytics.perAsset) ? analytics.perAsset : [];
  const dailyPnl = hasData && Array.isArray(analytics.dailyPnl) ? analytics.dailyPnl : [];

  // --- Calendar state ---
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth()); // 0-indexed

  const calMonthLabel = new Date(calYear, calMonth, 1).toLocaleString("default", { month: "long", year: "numeric" });

  const dailyPnlMap = Object.fromEntries(dailyPnl.map(({ date, pnl }) => [date, pnl]));

  function buildCalendarDays(year, month) {
    const firstDay = new Date(year, month, 1);
    // Mon=0 … Sun=6
    const startOffset = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = startOffset - 1; i >= 0; i--) {
      cells.push({ day: prevMonthDays - i, currentMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, currentMonth: true });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ day: cells.length - startOffset - daysInMonth + 1, currentMonth: false });
    }
    return cells;
  }

  const calendarCells = buildCalendarDays(calYear, calMonth);

  function calDateKey(day) {
    return `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_20%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_20%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Performance Intelligence"
          title="Read the journal like a trading business"
          description="A premium analytics screen should expose edge quality, risk pressure, and holding behavior clearly enough to change decisions, not just decorate the dashboard."
          action={
            <StatusPill tone={isFreePlan ? "warning" : "brand"}>
              <Activity className="mr-2 h-3.5 w-3.5" />
              {isFreePlan ? "Core analytics" : "Advanced analytics"}
            </StatusPill>
          }
        />
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Net P&L"
            value={loading ? "..." : (
              <DualCurrencyValue
                usdValue={analytics?.totalProfit}
                usdToNgnRate={usdToNgnRate}
                primaryClassName="text-current"
                secondaryClassName="text-xs text-slate-500 dark:text-slate-400"
              />
            )}
            helper="Primary USD view with NGN conversion underneath."
            tone={Number(analytics?.totalProfit || 0) >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}
          />
          <MetricCard label="Win Rate" value={loading ? "..." : `${analytics?.winRate || "0.00"}%`} helper="Outcome percentage without hiding trade quality." />
          <MetricCard
            label="Average Trade"
            value={loading ? "..." : (
              <DualCurrencyValue
                usdValue={analytics?.avgProfit}
                usdToNgnRate={usdToNgnRate}
                primaryClassName="text-current"
                secondaryClassName="text-xs text-slate-500 dark:text-slate-400"
              />
            )}
            helper="Mean trade result in USD, with NGN underneath."
          />
          <MetricCard label="Plan Tier" value={user?.plan ? user.plan.toUpperCase() : "FREE"} helper="Analytics depth expands with plan access." />
        </div>
      </SurfaceCard>

      {/* ── My Stats ── */}
      {!loading && hasData ? (
        <SurfaceCard>
          <SectionHeader eyebrow="My Stats" title="Streak &amp; calendar overview" description="Win/loss streaks and daily P&amp;L at a glance." />

          {/* Win / Loss ratio bar */}
          <div className="mt-6 space-y-1">
            <div className="flex justify-between text-xs font-medium text-slate-500 dark:text-slate-400">
              <span className="text-emerald-600 dark:text-emerald-400">Win {analytics.winRate}%</span>
              <span className="text-rose-600 dark:text-rose-400">Loss {(100 - Number(analytics.winRate)).toFixed(2)}%</span>
            </div>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full">
              <div className="bg-emerald-500" style={{ width: `${analytics.winRate}%` }} />
              <div className="flex-1 bg-rose-500" />
            </div>
          </div>

          {/* Max Win / Loss streak */}
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
            <div className="flex justify-between text-sm font-semibold text-slate-950 dark:text-white">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-1 w-4 rounded-full bg-emerald-500" />
                Max Win Streak
              </span>
              <span className="flex items-center gap-1.5">
                Max Loss Streak
                <span className="inline-block h-1 w-4 rounded-full bg-rose-500" />
              </span>
            </div>
            <div className="mt-3 flex justify-between text-3xl font-bold">
              <span className="text-slate-950 dark:text-white">{analytics.maxWinStreak ?? 0}</span>
              <span className="text-slate-950 dark:text-white">{analytics.maxLossStreak ?? 0}</span>
            </div>
            {(() => {
              const total = (analytics.maxWinStreak ?? 0) + (analytics.maxLossStreak ?? 0);
              const winPct = total ? ((analytics.maxWinStreak ?? 0) / total) * 100 : 50;
              return (
                <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full">
                  <div className="bg-emerald-500" style={{ width: `${winPct}%` }} />
                  <div className="flex-1 bg-rose-500" />
                </div>
              );
            })()}
          </div>

          {/* 2×2 stats grid */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Trades</p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">{analytics.tradeCount ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Win Trades</p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">{analytics.winTrades ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs font-medium text-rose-600 dark:text-rose-400">Loss Trades</p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">{analytics.lossTrades ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Max Drawdown</p>
              <p className="mt-1 text-2xl font-bold text-rose-600 dark:text-rose-400">
                {analytics.maxDrawdown != null ? formatUsd(analytics.maxDrawdown) : "—"}
              </p>
            </div>
          </div>

          {/* Monthly P&L Calendar */}
          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
                  else setCalMonth(m => m - 1);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-800"
              >
                ‹
              </button>
              <span className="text-sm font-semibold text-slate-950 dark:text-white">{calMonthLabel}</span>
              <button
                type="button"
                onClick={() => {
                  if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
                  else setCalMonth(m => m + 1);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-800"
              >
                ›
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-slate-400 dark:text-slate-500">
              {["Mo","Tu","We","Th","Fr","Sa","Su"].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-1">
              {calendarCells.map((cell, i) => {
                if (!cell.currentMonth) {
                  return <div key={i} className="aspect-square rounded-xl" />;
                }
                const key = calDateKey(cell.day);
                const pnl = dailyPnlMap[key];
                const hasTrade = pnl !== undefined;
                const isPos = hasTrade && pnl >= 0;
                const isNeg = hasTrade && pnl < 0;
                return (
                  <div
                    key={i}
                    className={`flex aspect-square flex-col items-center justify-center rounded-xl text-xs font-semibold transition ${
                      isPos
                        ? "bg-emerald-700 text-white"
                        : isNeg
                        ? "bg-rose-900 text-white"
                        : "bg-slate-800/40 text-slate-500 dark:bg-slate-700/30"
                    }`}
                  >
                    <span className="text-[11px] leading-none">{cell.day}</span>
                    {hasTrade && (
                      <span className="mt-0.5 text-[9px] leading-none opacity-90">
                        {pnl >= 0 ? "+" : ""}${Math.round(Math.abs(pnl))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SurfaceCard>
      ) : null}

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      {!loading && analytics?.message ? (
        <SurfaceCard>
          <SectionHeader
            eyebrow="No dataset yet"
            title="Analytics unlock once trades start flowing"
            description="Record enough executions to make the performance layer meaningful. Right now there is no journal data to analyze."
          />
        </SurfaceCard>
      ) : null}

      {!loading && hasData ? (
        <>
          {!isFreePlan ? (
            <section className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
              <SurfaceCard>
                <SectionHeader
                  eyebrow="Equity Curve"
                  title="Compounding path"
                  description="The curve shows whether recent execution quality is stacking consistently or slipping into unstable variance."
                />
                <div className="mt-6 h-[320px] w-full">
                  {equityCurve.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={equityCurve}>
                        <defs>
                          <linearGradient id="analyticsEquity" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.45} />
                            <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" opacity={0.2} />
                        <XAxis dataKey="index" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip />
                        <Area type="monotone" dataKey="equity" stroke="#0284c7" strokeWidth={3} fill="url(#analyticsEquity)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                      Equity data appears after more recorded trades.
                    </div>
                  )}
                </div>
              </SurfaceCard>

              <div className="grid gap-4">
                <SurfaceCard>
                  <SectionHeader eyebrow="Risk Pressure" title="Stability signals" description="These metrics reveal how much strain exists beneath headline win rate and total P&L." />
                  <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                    <MetricCard
                      label="Max Drawdown"
                      value={
                        <DualCurrencyValue
                          usdValue={analytics.maxDrawdown}
                          usdToNgnRate={usdToNgnRate}
                          primaryClassName="text-current"
                          secondaryClassName="text-xs text-slate-500 dark:text-slate-400"
                        />
                      }
                      helper="Largest peak-to-trough equity decline, shown in USD and NGN."
                      tone="text-rose-600 dark:text-rose-300"
                    />
                    <MetricCard label="Sharpe Ratio" value={String(analytics.sharpeRatio || 0)} helper="Return quality relative to overall volatility." tone="text-sky-600 dark:text-sky-300" />
                    <MetricCard label="Avg Hold" value={`${analytics.avgHoldHours || "0.00"}h`} helper="Average time capital remains exposed." tone="text-amber-600 dark:text-amber-300" />
                  </div>
                </SurfaceCard>

                <SurfaceCard>
                  <SectionHeader eyebrow="What to do with this" title="Interpretation guide" description="The goal is to turn metrics into operating decisions, not just to admire charts." />
                  <div className="mt-6 space-y-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                      <p className="font-semibold text-slate-950 dark:text-white">If drawdown is expanding faster than equity</p>
                      <p className="mt-2">Tighten risk per trade and review whether recent mistakes are concentrated in one setup family.</p>
                    </div>
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                      <p className="font-semibold text-slate-950 dark:text-white">If Sharpe is weak but win rate looks fine</p>
                      <p className="mt-2">Your winners may be too small relative to losses, or trade sizing may be unstable.</p>
                    </div>
                  </div>
                </SurfaceCard>
              </div>
            </section>
          ) : (
            <SurfaceCard>
              <SectionHeader
                eyebrow="Upgrade Insight"
                title="Free analytics cover outcomes, not the full operating picture"
                description="Upgrade to unlock equity-curve behavior, drawdown pressure, Sharpe ratio, holding distribution, and per-asset profitability."
                action={
                  <Button type="button" variant="brand" onClick={() => window.location.assign("/app/upgrade")}>
                    Unlock full analytics
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                }
              />
              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard label="Locked Module" value="Equity Curve" helper="See whether the account is compounding or chopping." />
                <MetricCard label="Locked Module" value="Drawdown" helper="Track risk compression and recovery quality." />
                <MetricCard label="Locked Module" value="Hold Analysis" helper="See how long trades stay exposed in practice." />
                <MetricCard label="Locked Module" value="Asset Edge" helper="Know which instruments are actually paying you." />
              </div>
            </SurfaceCard>
          )}

          {!isFreePlan ? (
            <section className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
              <SurfaceCard>
                <SectionHeader
                  eyebrow="Holding Distribution"
                  title="How long positions stay open"
                  description="This shows whether the trader is truly scalping, intraday, swinging, or drifting without clear structure."
                />
                <div className="mt-6 h-[280px] w-full">
                  {holdDistribution.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={holdDistribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" opacity={0.2} />
                        <XAxis dataKey="bucket" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#f59e0b" radius={[10, 10, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                      Hold-time distribution appears when entry and exit times are recorded.
                    </div>
                  )}
                </div>
              </SurfaceCard>

              <SurfaceCard>
                <SectionHeader
                  eyebrow="Per Asset"
                  title="Instrument-level edge map"
                  description="Separate what feels active from what is actually profitable so concentration decisions become evidence-based."
                />
                <div className="mt-6 space-y-4">
                  {perAsset.length ? perAsset.slice(0, 6).map((item) => (
                    <div key={item.asset} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.asset}</p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.count} recorded trades</p>
                        </div>
                        <StatusPill tone={Number(item.totalProfit) >= 0 ? "success" : "danger"}>{formatUsd(item.totalProfit)}</StatusPill>
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <MetricCard
                          label="P&L"
                          value={
                            <DualCurrencyValue
                              usdValue={item.totalProfit}
                              usdToNgnRate={usdToNgnRate}
                              primaryClassName="text-current"
                              secondaryClassName="text-xs text-slate-500 dark:text-slate-400"
                            />
                          }
                          className="p-3"
                        />
                        <MetricCard label="Win Rate" value={`${item.winRate}%`} className="p-3" />
                        <MetricCard label="Trade Count" value={String(item.count)} className="p-3" />
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                      Instrument breakdown appears after enough trades are categorized by asset.
                    </div>
                  )}
                </div>
              </SurfaceCard>
            </section>
          ) : null}

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SurfaceCard>
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
                <div>
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">Outcome quality</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Win rate matters only when average outcome stays healthy.</p>
                </div>
              </div>
            </SurfaceCard>
            <SurfaceCard>
              <div className="flex items-center gap-3">
                <TrendingDown className="h-5 w-5 text-rose-600 dark:text-rose-300" />
                <div>
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">Drawdown control</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">A strong journal protects against long equity recoveries.</p>
                </div>
              </div>
            </SurfaceCard>
            <SurfaceCard>
              <div className="flex items-center gap-3">
                <Clock3 className="h-5 w-5 text-amber-600 dark:text-amber-300" />
                <div>
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">Holding discipline</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Average hold time shows whether intent matches execution style.</p>
                </div>
              </div>
            </SurfaceCard>
            <SurfaceCard>
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-sky-600 dark:text-sky-300" />
                <div>
                  <p className="text-sm font-semibold text-slate-950 dark:text-white">Edge concentration</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Per-asset stats prevent false confidence from noisy winners.</p>
                </div>
              </div>
            </SurfaceCard>
          </section>
        </>
      ) : null}
    </div>
  );
}